// Lightweight i18n for SmartERP (English / Arabic).
// The tenant's preferred_language is exposed on user.tenant.preferred_language
// via /api/auth/me → useAuth(). applyDirForLang(lang) toggles document dir/lang for
// RTL layout and startDomTranslator(lang) live-translates rendered UI text using the
// generated dictionary in ./ar.json (English → Arabic). Data values (names, numbers,
// SKUs, dates) are left untouched because they are not in the dictionary.

import AR from "./ar.json";

const STRINGS = {
  "nav.dashboard":      { en: "Dashboard",         ar: "لوحة القيادة" },
  "nav.finance":        { en: "Finance",           ar: "المالية" },
  "nav.oman":           { en: "Oman Compliance",   ar: "الامتثال العُماني" },
  "nav.hr":             { en: "Human Resources",   ar: "الموارد البشرية" },
  "nav.scm":            { en: "Supply Chain",      ar: "سلسلة التوريد" },
  "nav.hse":            { en: "Health & Safety",   ar: "الصحة والسلامة" },
  "nav.eam":            { en: "Assets",            ar: "الأصول" },
  "nav.analytics":      { en: "Analytics",         ar: "التحليلات" },
  "nav.org":            { en: "Organization",      ar: "المنظمة" },
  "nav.workflows":      { en: "Workflows",         ar: "سير العمل" },
  "nav.approvals":      { en: "Approvals",         ar: "الموافقات" },
  "nav.audit":          { en: "Audit Log",         ar: "سجل التدقيق" },
  "nav.settings":       { en: "Settings",          ar: "الإعدادات" },
  "common.save":        { en: "Save",              ar: "حفظ" },
  "common.cancel":      { en: "Cancel",            ar: "إلغاء" },
  "common.loading":     { en: "Loading…",          ar: "جارٍ التحميل…" },
};

export function t(key, lang = "en") {
  const s = STRINGS[key];
  if (!s) return key;
  return s[lang] || s.en || key;
}

export function applyDirForLang(lang) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("dir", lang === "ar" ? "rtl" : "ltr");
  document.documentElement.setAttribute("lang", lang);
}

// ---------------------------------------------------------------- dictionary lookup
const DICT = new Map(Object.entries(AR));
const LOWER = new Map();
const STRIP_RE = /[\s:…·.!?]+$/;
for (const [k, v] of DICT) {
  const lk = k.toLowerCase(); if (!LOWER.has(lk)) LOWER.set(lk, v);
  const bare = k.replace(STRIP_RE, ""), bv = v.replace(STRIP_RE, "");
  if (bare !== k && bare && !LOWER.has(bare.toLowerCase())) LOWER.set(bare.toLowerCase(), bv);
}
for (const s of Object.values(STRINGS)) { if (!DICT.has(s.en)) { DICT.set(s.en, s.ar); LOWER.set(s.en.toLowerCase(), s.ar); } }

const TRAIL_RE = /^(\s*)(.*?)([\s:…·.!?]*)$/s;
const COUNT_RE = /^(\d[\d,.]*)\s+(.+)$/;          // "12 rows", "3 pinned"
const SUFFIX_RE = /^(.+?)\s+(\(\d+\)|#|\d+)$/;    // "CREATE DRAFT POs (2)", "PO #"

export function translateText(text) {
  if (!text || !/[A-Za-z]{2}/.test(text)) return null;
  const m = TRAIL_RE.exec(text);
  const lead = m[1], core = m[2], trail = m[3];
  const hit = DICT.get(core) ?? LOWER.get(core.toLowerCase());
  if (hit) return lead + hit + trail;
  const c = COUNT_RE.exec(core);
  if (c) { const h = DICT.get(c[2]) ?? LOWER.get(c[2].toLowerCase()); if (h) return `${lead}${c[1]} ${h}${trail}`; }
  const sfx = SUFFIX_RE.exec(core);
  if (sfx) { const h = DICT.get(sfx[1]) ?? LOWER.get(sfx[1].toLowerCase()); if (h) return `${lead}${h} ${sfx[2]}${trail}`; }
  if (core.includes(" · ")) {
    const parts = core.split(" · "); let any = false;
    const out = parts.map((p) => { const h = translateText(p); if (h) any = true; return h ?? p; });
    if (any) return lead + out.join(" · ") + trail;
  }
  // word-wise fallback for composed labels ("CREATE PO", "SAVE SO"): every word must be known or an abbreviation/number
  const words = core.split(/\s+/);
  if (words.length >= 2 && words.length <= 5) {
    let any = false;
    const out = words.map((w) => {
      const h = DICT.get(w) ?? LOWER.get(w.toLowerCase());
      if (h) { any = true; return h; }
      return ACRONYMS.has(w.replace(/[^A-Za-z0-9]/g, "")) || !/[A-Za-z]/.test(w) ? w : null;
    });
    if (any && out.every((w) => w !== null)) return lead + out.join(" ") + trail;
  }
  return null;
}

// ---------------------------------------------------------------- live DOM translator
const ACRONYMS = new Set(["PO", "POs", "SO", "SOs", "SKU", "SKUs", "GRN", "VAT", "IFRS", "AR", "AP", "GL", "HR", "SCM", "AML", "HSE", "EAM", "CSV", "XLSX", "PDF", "XML", "ID", "MFA", "SSO", "API", "KPI", "ECL", "MT940", "OMR", "USD", "YTD", "Q1", "Q2", "Q3", "Q4", "OTA", "UBL", "QR", "IBAN", "H2H", "SmartERP", "Fawtara", "Peppol", "Tap"]);
const SKIP_TAGS = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "TEXTAREA", "CODE", "PRE", "SVG", "PATH"]);
const ATTRS = ["placeholder", "title", "aria-label"];
const originals = new WeakMap();   // text node -> last English value we translated
let observer = null;

function translateNode(node) {
  if (node.nodeType === Node.TEXT_NODE) {
    const parent = node.parentElement;
    if (!parent || SKIP_TAGS.has(parent.tagName) || parent.closest("[data-no-i18n], .notranslate")) return;
    const val = node.nodeValue;
    if (originals.get(node) === val) return;           // already translated (React wrote the same value)
    const out = translateText(val);
    if (out && out !== val) { node.nodeValue = out; originals.set(node, out); }
    return;
  }
  if (node.nodeType !== Node.ELEMENT_NODE || SKIP_TAGS.has(node.tagName)) return;
  for (const a of ATTRS) {
    const v = node.getAttribute?.(a);
    if (v) { const out = translateText(v); if (out && out !== v) node.setAttribute(a, out); }
  }
  if (node.tagName === "INPUT" && (node.type === "submit" || node.type === "button") && node.value) {
    const out = translateText(node.value); if (out) node.value = out;
  }
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let n;
  while ((n = walker.nextNode())) {
    if (n.nodeType === Node.TEXT_NODE) translateNode(n);
    else if (n.nodeType === Node.ELEMENT_NODE) {
      for (const a of ATTRS) { const v = n.getAttribute(a); if (v) { const out = translateText(v); if (out && out !== v) n.setAttribute(a, out); } }
    }
  }
}

export function startDomTranslator(lang) {
  if (typeof document === "undefined") return;
  if (observer) { observer.disconnect(); observer = null; }
  if (lang !== "ar") return;
  translateNode(document.body);
  observer = new MutationObserver((records) => {
    for (const r of records) {
      if (r.type === "characterData") translateNode(r.target);
      else if (r.type === "attributes") translateNode(r.target);
      else r.addedNodes.forEach((n) => translateNode(n));
    }
  });
  observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ATTRS });
}

export const dictionarySize = DICT.size;

// ---------------------------------------------------------------- locale-aware numbers & dates
// Arabic uses the Omani Arabic locale with Latin digits (Gulf business convention) so
// amounts stay copy/paste-safe while separators, month names and RTL ordering follow Arabic.
export const currentLang = () => (typeof document !== "undefined" && document.documentElement.getAttribute("lang") === "ar" ? "ar" : "en");
export const numberLocale = () => (currentLang() === "ar" ? "ar-OM-u-nu-latn" : "en-US");
export function fmtNumber(n, opts = {}) {
  return Number(n || 0).toLocaleString(numberLocale(), opts);
}
export function fmtDate(value, opts = { year: "numeric", month: "short", day: "2-digit" }) {
  if (!value) return "";
  const s = String(value);
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  if (Number.isNaN(d.getTime())) return s;
  return currentLang() === "ar" ? d.toLocaleDateString("ar-OM-u-nu-latn", opts) : s.slice(0, 10);
}
